import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Container,
  Tooltip,
} from "@mui/material";
import { ArrowBack, Visibility, HelpOutline } from "@mui/icons-material";
import axiosInstance from "../../axios/axiosInstance";
import SkillMatchingDialog from "./SkillMatchingDialog";

const SkillMatchingPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [matchData, setMatchData] = useState([]);
  const [jobDetails, setJobDetails] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchSkillMatchingDetails = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/api/job-postings/${jobId}/match-candidates`,
      );
      setMatchData(res.data || []);
      // Assuming job details might be in the response or set it separately
      if (res.data.length > 0) {
        setJobDetails({
          id: jobId,
          job_role_name: res.data[0]?.candidate?.role_name || "Job Role",
        });
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching skill match data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchSkillMatchingDetails();
    }
  }, [jobId]);

  const getLabelColor = (gate_fail) => {
    return gate_fail ? "error" : "success";
  };

  const handleViewDetails = (candidate) => {
    setSelectedCandidate(candidate);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCandidate(null);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Job Postings
        </Button>
        <Typography variant="h4" gutterBottom>
          Skill Matching Results
        </Typography>
        {jobDetails && (
          <Typography variant="subtitle1" color="text.secondary">
            Job Role: {jobDetails.job_role_name}
          </Typography>
        )}
      </Box>

      {matchData.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Candidate Name</TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    Status
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Status
                          </Typography>

                          <Typography variant="body2" paragraph>
                            Shows how well your profile matches this role, based
                            on your Overall Score:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                            <li>
                              <Typography variant="body2">
                                Strong Match → 0.90–1.35
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2">
                                Good Match → 0.75–0.89
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2">
                                Consider → 0.60–0.74
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2">
                                Not a Fit → Below 0.60
                              </Typography>
                            </li>
                          </ul>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <HelpOutline
                        fontSize="small"
                        color="action"
                        sx={{ cursor: "help", fontSize: 16 }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    Technical Score
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Technical Score
                          </Typography>
                          <Typography variant="body2" paragraph>
                            Based on how closely Candidate's skills, sub-skills,
                            and proficiency levels match the role’s required
                            technical skills.
                          </Typography>
                          <Typography variant="body2">
                            Missing must-have skills lowers this score
                            significantly.
                          </Typography>
                          <Typography variant="body2">
                            Max Technical Score Possible : 1.5
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <HelpOutline
                        fontSize="small"
                        color="action"
                        sx={{ cursor: "help", fontSize: 16 }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    Soft Score
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Soft Score
                          </Typography>
                          <Typography variant="body2">
                            Calculated from how many of Candidate's soft skills
                            match the role’s required soft skills.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <HelpOutline
                        fontSize="small"
                        color="action"
                        sx={{ cursor: "help", fontSize: 16 }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    Environment Score
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Environment Score
                          </Typography>
                          <Typography variant="body2" paragraph>
                            Measures how well your work preferences align with
                            the role’s environment, based on:
                          </Typography>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: "1.2rem",
                              marginBottom: "8px",
                            }}
                          >
                            <li>
                              <Typography variant="body2">
                                Preferred work environment (office, hybrid,
                                remote)
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2">
                                Company type and work culture
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2">
                                Work setup compatibility
                              </Typography>
                            </li>
                          </ul>
                          <Typography variant="body2">
                            Better alignment with the role’s environment leads
                            to a higher score.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <HelpOutline
                        fontSize="small"
                        color="action"
                        sx={{ cursor: "help", fontSize: 16 }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    Overall Score
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Overall Score
                          </Typography>
                          <Typography variant="body2" paragraph>
                            Your overall score is calculated using a weighted
                            ratio:
                          </Typography>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: "1.2rem",
                              marginBottom: "8px",
                            }}
                          >
                            <li>
                              <Typography variant="body2">
                                Technical Score: ~70% of the total score
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2">
                                Soft Score: ~20% of the total score
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2">
                                Environment Score: ~10% of the total score
                              </Typography>
                            </li>
                          </ul>
                          <Typography variant="body2" paragraph>
                            A small technical bonus can be added for strong or
                            exceeding skill matches.
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Maximum possible score: 1.350
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <HelpOutline
                        fontSize="small"
                        color="action"
                        sx={{ cursor: "help", fontSize: 16 }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matchData.map((candidate, index) => (
                <TableRow
                  key={candidate.candidate_id || index}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {candidate.candidate?.name ||
                      candidate.candidate?.email_masked ||
                      "Unknown"}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={candidate.scores?.label || "Unknown"}
                      // color={getLabelColor(candidate.scores?.gate_fail)}
                      size="small"
                      sx={{
                        backgroundColor:
                          candidate.scores?.gate_fail ||
                          candidate.scores?.label === "Not a Fit"
                            ? "#ba68c8" // Rejected/Fail/Missing
                            : candidate.scores?.label === "Strong Match"
                              ? "#9b42e9" // Success/Strong
                              : "#8c5eb4", // Good/Consider/Neutral
                        color: "#FFFFFF",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {candidate.scores?.technical_score}
                  </TableCell>
                  <TableCell align="center">
                    {candidate.scores?.soft_score}
                  </TableCell>
                  <TableCell align="center">
                    {candidate.scores?.environment_score}
                  </TableCell>
                  <TableCell align="center">
                    <strong>{candidate.scores?.overall_score}</strong>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleViewDetails(candidate)}
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No matching candidates found.
          </Typography>
        </Paper>
      )}

      {/* Dialog for detailed view */}
      {selectedCandidate && (
        <SkillMatchingDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          candidate={selectedCandidate}
          job={jobDetails}
        />
      )}
    </Container>
  );
};

export default SkillMatchingPage;
